import { userProfile$ } from '@psl/functions/firebase/observables/userProfile$';
import { userCategorizedResourcesQuery } from '@psl/functions/firebase/queries/user/userCategorizedResourcesQuery';
import { defer, from, Observable, ObservableInput, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { Profile, ProfileUserType } from '../../models/ClientModels';
import { shareReplay } from '../../utils/rxExtensions';
import { auth, firestore, User } from '../firebase/firebaseAccess';
import {
  collectionDataWithId,
  docDataWithId,
  WithID,
} from '../firebase/rxfireExtensions';
import { useObservable } from '../hooks/useObservable';

export const authState$: Observable<User | null> = defer(
  () =>
    new Observable<User | null>(function (subscriber) {
      const unsubscribe = auth.onAuthStateChanged({
        next: v => {
          subscriber.next(v);
        },
        error: e => subscriber.error(e),
        complete: () => subscriber.complete(),
      });
      return { unsubscribe: unsubscribe };
    }),
).pipe(shareReplay(1));

export const currentUserId$ = from(authState$).pipe(
  map(user => user?.uid),
  distinctUntilChanged(),
  shareReplay(1),
);

export const currentUserToken$ = from(authState$).pipe(
  switchMap(user => (user ? from(user.getIdToken()) : of(undefined))),
  distinctUntilChanged(),
  shareReplay(1),
);

export const currentUserTokenResult$ = from(authState$).pipe(
  switchMap(user => (user ? from(user.getIdTokenResult()) : of(undefined))),
  distinctUntilChanged(),
  shareReplay(1),
);

export function skipForUnauthenticatedUsers<T>(
  func: (uid: string) => ObservableInput<T>,
  defaultValue: T,
): Observable<T> {
  return currentUserId$.pipe(
    switchMap(uid => (uid == null ? of(defaultValue) : func(uid))),
  );
}

export const currentUserId = () => auth.currentUser?.uid;

export const getCurrentUser = () => auth.currentUser;

export const useAuthStatus = () => {
  return useObservable(authState$, 'app-auth-user-state', null);
};

export const useCurrentUserId = () =>
  useObservable(currentUserId$, 'current-user-id');

export const useIsAnonymousUser = () => {
  const profile = useCurrentUserProfile();
  return profile == null || profile.userType === ProfileUserType.guest;
};

export const useCurrentUserAuthToken = () =>
  useObservable(currentUserToken$, 'current-user-auth-token');

export const useObservableWithCurrentUser = <T>(
  factory: (uid: string) => Observable<T>,
  observableId: string,
  prediction?: (user: User) => boolean,
) => {
  return useObservable(
    inputs$ =>
      inputs$.pipe(
        switchMap(([user$]) =>
          user$.pipe(
            switchMap(user =>
              user?.uid && (prediction == null || prediction(user))
                ? factory(user.uid)
                : of(undefined),
            ),
          ),
        ),
      ),
    [authState$],
    observableId,
  );
};

export const useCurrentUserAuthTokenResult = () =>
  useObservable(currentUserTokenResult$, 'current-user-auth-token-result');

export const useIsCurrentUserAnonymous = () => {
  const tokenResult = useCurrentUserAuthTokenResult();
  if (tokenResult == null) return true;
  return tokenResult.claims['isGuest'] === true;
};

export const useIsCurrentUserAdminLogin = () => {
  const tokenResult = useCurrentUserAuthTokenResult();
  if (tokenResult == null) return false;
  return tokenResult.claims['isPSLAdmin'] === true;
};

export const isCurrentUserAnonymous = async () => {
  const user = getCurrentUser();
  if (user == null) return true;
  const tokenResult = await user.getIdTokenResult();
  return tokenResult.claims['isGuest'] === true;
};

export const isCurrentUserGuest = async () => {
  const user = getCurrentUser();
  if (user == null) return false;
  const tokenResult = await user.getIdTokenResult();
  return tokenResult.claims['isGuest'] === true;
};

export const useIsCurrentUserGuest = () => {
  const tokenResult = useCurrentUserAuthTokenResult();
  if (tokenResult == null) return false;
  return tokenResult.claims['isGuest'] === true;
};

export const currentUserProfile$ = currentUserId$.pipe(
  switchMap(uid => (uid == null ? of(null) : userProfile$(uid))),
);

export const useCurrentUserProfile = () => {
  return useObservable(currentUserProfile$, 'app-auth-current-user-profile');
};

export const currentUserPrivateProfile$ = currentUserId$.pipe(
  switchMap(uid =>
    uid == null
      ? of(null)
      : docDataWithId(firestore().collection('privateProfiles').doc(uid)),
  ),
  shareReplay(1),
);

export const useCurrentUserPrivateProfile = (enabled = true) => {
  return useObservableWithCurrentUser(
    uid =>
      enabled
        ? docDataWithId(
            firestore().collection('privateProfiles').doc(uid),
          ).pipe(shareReplay(1))
        : of(undefined),
    `app-auth-current-user-private-profile${enabled ? '' : '-disabled'}`,
  );
};

export const currentUserReadonlyProfile$ = currentUserId$.pipe(
  switchMap(uid =>
    uid == null
      ? of(null)
      : docDataWithId(firestore().collection('readonlyProfiles').doc(uid)),
  ),
  shareReplay(1),
);

export const useCurrentUserReadonlyProfile = (enabled = true) =>
  useObservableWithCurrentUser(
    uid =>
      enabled
        ? docDataWithId(
            firestore().collection('readonlyProfiles').doc(uid),
          ).pipe(shareReplay(1))
        : of(undefined),
    `app-auth-current-user-readonly-profile${enabled ? '' : '-disabled'}`,
  );

export const currentUserCategories$ = currentUserId$.pipe(
  switchMap(uid =>
    uid == null
      ? of([])
      : collectionDataWithId(userCategorizedResourcesQuery(uid)),
  ),
);

export const useCurrentUserCategories = () => {
  return useObservable(currentUserCategories$, 'current-user-categories', null);
};

export const useUserProfile = (
  uid: string | null | undefined,
  startValue?: WithID<Profile> | null,
) =>
  useObservable(
    uid ? userProfile$(uid) : of(null),
    `users/${uid || '__none__'}/profile`,
    startValue,
  );
